import React, { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { AdminInput } from 'shared-components';
import useFormData from 'hooks/useFormData';
import VariantImages from '../VariantImages/VariantImages';
import ColorPicker from '../ColorPicker/ColorPicker';
import styles from './VariantForm.module.css';

const CHARACTER_LIMITS = {
  SKU: 19,
};

const WORD_ATTRIBUTE_LABELS = {
  0: 'First Word Attribute',
  1: 'Second Word Attribute',
  2: 'Third Word Attribute',
  3: 'Fourth Word Attribute',
  4: 'Fifth Word Attribute',
};

const VariantForm = ({
  handleUpdateVariant,
  index,
  initialFormData,
  variantErrors,
  requiredPublicVariantFields,
  removeVariantError,
  colorAttributeCount,
  wordAttributesCount,
}) => {
  const {
    handleInputChange,
    formData,
    formErrors,
    setFormData,
    resetField,
    handleErrors,
  } = useFormData();
  const {
    colorAttribute,
    colorAttribute2,
    colorAttributeDescriptor,
    wordAttributes,
    sku,
    legacyPostId,
    retailPrice,
    galleryUrls,
    privateImageUrls,
    imageUrl,
  } = initialFormData;

  const {
    colorAttribute: colorAttributeError,
    colorAttributeDescriptor: colorAttributeDescriptorError,
    sku: skuError,
    retailPrice: retailPriceError,
  } = variantErrors;
  const {
    sku: skuRequired,
    retailPrice: retailPriceRequired,
    colorAttributeDescriptor: colorAttributeDescriptorRequired,
    thumbnailImage: thumbnailImageRequired,
  } = requiredPublicVariantFields;
  const [displaySecondColorPicker, setDisplaySecondColorPicker] =
    useState(false);
  //this updates the parent form with this form's data
  useEffect(() => {
    handleUpdateVariant(formData, formErrors, index);
  }, [formData, formErrors]);

  //this updates the form data with response from BE or when reordered
  useEffect(() => {
    if (!_.isEqual(initialFormData, formData)) {
      setFormData(initialFormData);
    }
  }, [initialFormData]);

  //remove second color attribute if empty
  useEffect(() => {
    if (!colorAttribute2) {
      setDisplaySecondColorPicker(false);
    }
    if (colorAttribute2) {
      setDisplaySecondColorPicker(true);
    }
  }, [colorAttribute2]);

  useEffect(() => {
    if (colorAttribute === '') {
      setDisplaySecondColorPicker(false);
      setFormData((prevState) => {
        return {
          ...prevState,
          colorAttribute: prevState.colorAttribute2,
          colorAttribute2: '',
        };
      });
    }
  }, [colorAttribute]);

  useEffect(() => {
    if (wordAttributesCount) {
      const wordCount = parseInt(wordAttributesCount, 10);
      const getNewWordAttributes = (prevState) => {
        let newAttributes = [];
        if (prevState.wordAttributes) {
          newAttributes = prevState.wordAttributes.slice(0, wordCount);
        }
        for (let i = 0; i < wordCount; i += 1) {
          //assign empty string to any undefined values in the array
          if (!newAttributes[i]) {
            newAttributes[i] = '';
          }
        }
        return newAttributes;
      };
      setFormData((prevState) => {
        const newAttributes = getNewWordAttributes(prevState);
        return {
          ...prevState,
          wordAttributes: newAttributes,
        };
      });
    }
  }, [wordAttributesCount]);

  useEffect(() => {
    if (colorAttributeCount === '0') {
      //if color attribute count is 0 we want to remove fields from form
      setFormData((prevState) => {
        const newState = _.cloneDeep(prevState);
        delete newState.colorAttributeDescriptor;
        delete newState.colorAttribute;
        delete newState.colorAttribute2;
        return newState;
      });
    }
    //if color attribute is 1 we want to give those values empty strings to pass to BE
    if (colorAttributeCount === '1') {
      setFormData((prevState) => {
        const newState = _.cloneDeep(prevState);
        //we only want to do this if values don't already exist so we don't overwrite them
        if (_.isNil(newState.colorAttributeDescriptor)) {
          newState.colorAttributeDescriptor = '';
        }
        if (_.isNil(newState.colorAttribute)) {
          newState.colorAttribute = '';
        }
        if (_.isNil(newState.colorAttribute2)) {
          newState.colorAttribute2 = '';
        }
        return newState;
      });
    }
  }, [colorAttributeCount]);

  const handleChange = (e) => {
    //remove parent error on edit field
    removeVariantError(e.target.name, index);
    handleInputChange(e);
  };

  const handleWordAttribute = (e, index) => {
    e.persist();
    const handleAttributes = (prevState) => {
      let newAttributes = [];
      if (prevState.wordAttributes) {
        newAttributes = [...prevState.wordAttributes];
      }
      newAttributes[index] = e.target.value;
      return newAttributes;
    };
    setFormData((prevState) => {
      const newAttributes = handleAttributes(prevState);
      return {
        ...prevState,
        wordAttributes: newAttributes,
      };
    });
  };

  const handleColorChange = (color, name) => {
    removeVariantError(name, index);
    setFormData((prevState) => {
      return {
        ...prevState,
        [name]: color.hex,
      };
    });
  };

  const handleSku = (e) => {
    handleChange(e);
    const { value } = e.target;
    //const matches = /^[a-zA-Z0-9](-?[a-zA-Z0-9])*$/.test(value); this is the correct req expression - below one is temporary
    const matches = /^[a-zA-Z0-9](-?\/?[a-zA-Z0-9])*$/.test(value);
    if (value && !matches)
      handleErrors({
        sku: 'Please enter a valid SKU.',
      });
  };

  return (
    <div className={styles.container}>
      <p>Variant Details</p>
      <div className={styles.form}>
        {colorAttributeCount === '1' && (
          <>
            <AdminInput
              label="Color Attribute Descriptor"
              value={colorAttributeDescriptor || ''}
              type="text"
              onChange={handleChange}
              name="colorAttributeDescriptor"
              error={
                colorAttributeDescriptorError ||
                colorAttributeDescriptorRequired ||
                null
              }
            />
            <ColorPicker
              value={colorAttribute}
              handleColorChange={handleColorChange}
              handleInputChange={handleChange}
              colorAttributeError={colorAttributeError}
              label="Color Attribute"
              openSecondColorPicker={() => setDisplaySecondColorPicker(true)}
              name="colorAttribute"
              firstColor={colorAttribute}
              secondColor={colorAttribute2}
              resetField={resetField}
              displaySecondColorPicker={displaySecondColorPicker}
              error={colorAttributeError}
            />
            {displaySecondColorPicker && (
              <ColorPicker
                value={colorAttribute2}
                handleColorChange={handleColorChange}
                handleInputChange={handleChange}
                label="Additional Color"
                name="colorAttribute2"
                firstColor={colorAttribute}
                secondColor={colorAttribute2}
                resetField={resetField}
              />
            )}
          </>
        )}
        {_.map(wordAttributes, (wordAttribute, index) => (
          <AdminInput
            label={WORD_ATTRIBUTE_LABELS[index]}
            value={wordAttribute || ''}
            type="text"
            onChange={(e) => handleWordAttribute(e, index)}
            name="wordAttribute"
            key={index}
          />
        ))}
        <AdminInput
          label="SKU"
          value={sku || ''}
          characterLimit={CHARACTER_LIMITS.SKU}
          type="text"
          onChange={handleSku}
          name="sku"
          error={skuError || skuRequired || null}
        />
        <AdminInput
          label="Product ID"
          value={legacyPostId || ''}
          type="text"
          name="productId"
          disabled
        />
        <AdminInput
          label="Retail Price"
          value={retailPrice || ''}
          type="price"
          name="retailPrice"
          onChange={handleChange}
          error={retailPriceError || retailPriceRequired || null}
        />
      </div>
      <p>
        Product Images
        <span
          data-tip="<div>Guidelines For Uploading Images</div>
          <span>Images must be in PNG, GIF or JPEG format.</span>
          <span>File size must be 500KB or less.</span>
          <span>Image must be at least 800 x 800.</span>
          <span>Image must be a square.</span>"
          data-for="imageInfo"
        >
          <i className="fas fa-info-circle" />
        </span>
      </p>
      <VariantImages
        galleryUrls={galleryUrls}
        privateImageUrls={privateImageUrls}
        imageUrl={imageUrl}
        setFormData={setFormData}
        thumbnailError={thumbnailImageRequired}
      />
      <ReactTooltip
        id="imageInfo"
        place="top"
        type="dark"
        effect="solid"
        //eslint-disable-next-line
        html={true}
        className={styles.tooltip}
      />
    </div>
  );
};

VariantForm.propTypes = {
  handleUpdateVariant: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  initialFormData: PropTypes.shape({}),
  variantErrors: PropTypes.shape({}),
  requiredPublicVariantFields: PropTypes.shape({}),
  removeVariantError: PropTypes.func.isRequired,
  colorAttributeCount: PropTypes.string,
  wordAttributesCount: PropTypes.string,
};

VariantForm.defaultProps = {
  initialFormData: {},
  colorAttributeCount: '',
  wordAttributesCount: '',
  variantErrors: {},
  requiredPublicVariantFields: {},
};

export default VariantForm;
